import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { Button } from '../../../../shared/components/index.js';

interface SearchHit {
  readonly searchIndexId: string;
  readonly indexCode: string;
  readonly sourceModule: string;
  readonly contentType: string;
  readonly title: string;
  readonly snippet: string;
  readonly relevanceScore: number;
}
interface ModuleGroup {
  readonly sourceModule: string;
  readonly resultCount: number;
  readonly topResults: readonly SearchHit[];
}
interface FederatedOutcome {
  readonly searchQueryId: string;
  readonly query: string;
  readonly searchType: string;
  readonly totalResults: number;
  readonly executionMs: number;
  readonly modules: readonly ModuleGroup[];
  readonly results: readonly SearchHit[];
}

const MODULES = ['DMS', 'KMS', 'DAM', 'ANALYTICS', 'CONTRACTS', 'CAMPAIGNS', 'VENDORS', 'WORKFLOWS', 'APPROVALS', 'PROJECTS'];

/**
 * M39 — Federated Search Dashboard (§9/§23). Runs a single query across many
 * source modules at once and presents the hits grouped by originating module,
 * each capped by a per-module limit so no single system dominates the page. The
 * cross-module reach is ACL-trimmed per module (BR-02: source permissions are
 * honoured), ranked (§13) and logged (BR-03). Requires SEARCH_EXECUTE; a 403
 * surfaces the required permission.
 */
export function FederatedSearchDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();

  const { number } = useBusinessNumber();
  const [query, setQuery] = useState('');
  const [modules, setModules] = useState<string[]>([]);
  const [perModuleLimit, setPerModuleLimit] = useState(5);
  const [outcome, setOutcome] = useState<FederatedOutcome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (value: string): void => {
    setModules((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));
  };

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setOutcome(null);
    if (!query.trim()) { setError(t('m39.federated.enterQuery')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: FederatedOutcome }>(`/v1/search/federated`, {
        method: 'POST',
        body: {
          query: query.trim(),
          sourceModules: modules.length ? modules : undefined,
          perModuleLimit,
        },
        token,
      });
      setOutcome(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError(t('m39.federated.notAuthorised'));
      } else {
        setError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="federated-search-dashboard">
      <h1>{t('m39.federated.title')}</h1>

      <form aria-label="federated-form" onSubmit={(e) => void submit(e)}>
        <label>{t('m39.common.query')}
          <input aria-label="field-query" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('m39.federated.queryPlaceholder')} />
        </label>
        <label>{t('m39.federated.perModuleLimit')}
          <input type="number" aria-label="field-limit" min={1} max={50} value={perModuleLimit}
            onChange={(e) => setPerModuleLimit(Math.max(1, Number(e.target.value) || 1))} />
        </label>
        <fieldset aria-label="field-modules">
          <legend>{t('m39.federated.modulesLegend')}</legend>
          {MODULES.map((m) => (
            <label key={m}>
              <input type="checkbox" aria-label={`module-${m}`} checked={modules.includes(m)} onChange={() => toggle(m)} />{m}
            </label>
          ))}
        </fieldset>
        <Button variant="primary" type="submit" aria-label="submit-federated" disabled={busy}>{busy ? t('m39.federated.federating') : t('m39.federated.federate')}</Button>
      </form>

      {error && <p role="alert" aria-label="federated-error">{error}</p>}

      {outcome && (
        <section aria-label="federated-results">
          <p aria-label="federated-summary">
            {t('m39.federated.summary', { total: outcome.totalResults, modules: outcome.modules.length, ms: outcome.executionMs })}
          </p>
          {outcome.modules.length === 0 ? (
            <p aria-label="federated-empty">{t('m39.federated.empty')}</p>
          ) : (
            <div className="federated-grid">
              {outcome.modules.map((g) => (
                <article key={g.sourceModule} aria-label={`group-${g.sourceModule}`} data-module={g.sourceModule}>
                  <h2>{g.sourceModule} <span aria-label={`group-count-${g.sourceModule}`}>({g.resultCount})</span></h2>
                  {g.topResults.length === 0 ? (
                    <p>{t('m39.federated.noHits')}</p>
                  ) : (
                    <ul>
                      {g.topResults.map((hit) => (
                        <li key={hit.searchIndexId} data-result={hit.indexCode}>
                          <strong>{hit.title}</strong>
                          <span aria-label={`fed-score-${hit.indexCode}`}>{number(hit.relevanceScore, 3)}</span>
                          <p>{hit.snippet}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
